import axios from "axios";
import store from "../../store/store.js";
import router from "../../routes";
import { isRecentAuthenticationChallengeReason, requestRecentAuthentication } from "../RecentAuthenticationRetry.mjs";
import { retryAfterCsrfRefresh } from "../CsrfRetry.mjs";
import { isApplicationVersionError, isMaintenanceError, mfaChallengeRedirectFromError } from "../../auth/mfaVerificationResult.mjs";

function currentRoute() {
    return router.currentRoute?.value || router.currentRoute;
}

function handlePaymentsErrors(response) {
    store.commit("errors/setResponse", response);

    if (response && response.data && response.data.message && response.data.message.startsWith("licences.")) {
        if (currentRoute().name !== "errors.licences") {
            router.push({
                name: "errors.licences",
                params: {
                    ...currentRoute().params,
                    public: currentRoute().meta && currentRoute().meta.public ? currentRoute().meta.public : false,
                },
            });
        }

        return;
    }

    store.commit("errors/setResponse", null);
}

function handleAuthErrors(response) {
    if (response && response.data && response.data.message) {
        switch (response.data.message) {
            case "auth.required":
                if (currentRoute().name !== "public.auth.login") {
                    router.push({ name: "public.auth.login", query: { redirect: window.location.href } });
                }
                break;
        }
    }
}

function handleMfaErrors(response) {
    const challengeRedirect = mfaChallengeRedirectFromError(response);

    if (challengeRedirect !== null) {
        window.location.href = challengeRedirect;
        return;
    }

    if (response?.data?.redirect_url && response.data.redirect_url.includes("/login") && currentRoute().name !== "public.auth.login") {
        const loginUrl = new URL(response.data.redirect_url, window.location.origin);

        if (response.data.reason === "admin_domain_required") {
            loginUrl.searchParams.set("auth_error", response.data.reason);
        }

        window.location.href = `${loginUrl.pathname}${loginUrl.search}`;
    }
}

function handleAppVersionErrors(response) {
    document.location.reload();
}

function handleInMaintenance(response) {
    document.location.reload();
}

export default function setup() {
    axios.interceptors.response.use(
        function (response) {
            return response;
        },
        function (err) {
            if (err && err.response) {
                if (err.response.status === 419 && !err.config?.__csrfRefreshRequest) {
                    return retryAfterCsrfRefresh(
                        err,
                        () => axios.get("/password/confirm", { __csrfRefreshRequest: true }),
                        (config) => axios.request(config)
                    );
                }

                if (mfaChallengeRedirectFromError(err.response) !== null) {
                    handleMfaErrors(err.response);

                    return Promise.reject(err);
                }

                switch (err.response.status) {
                    case 402:
                        handlePaymentsErrors(err.response);
                        break;
                    case 401:
                        handleAuthErrors(err.response);
                        break;
                    case 403:
                        handleMfaErrors(err.response);
                        break;
                    case 429:
                        handleMfaErrors(err.response);
                        break;
                    case 423:
                        if (isRecentAuthenticationChallengeReason(err.response.data?.reason) && !err.config?.__recentAuthRetried) {
                            return requestRecentAuthentication(err.config, err.response.data);
                        }
                        break;
                    case 410:
                        if (isApplicationVersionError(err.response)) {
                            handleAppVersionErrors(err.response);
                        }
                        break;
                    case 503:
                        if (isMaintenanceError(err.response)) {
                            handleInMaintenance(err.response);
                        }
                        break;
                    default:
                        break;
                }
            }

            return Promise.reject(err);
        }
    );
}
